import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useApi, useFetcher, type Me, type Organisation } from "../api.js";
import { useAuth } from "../auth.js";
import { Button, ErrorBox, Loading, useAction } from "../components/ui.js";

export function SignIn() {
  const auth = useAuth();
  return (
    <div className="centered">
      <div className="panel">
        <img src="/favicon.svg" alt="" width={40} height={40} />
        <h1>Forge</h1>
        <p className="muted">Builds, deployments and releases for REPU-ME products.</p>
        <Button
          variant="primary"
          onClick={() => {
            void auth.signIn();
          }}
        >
          {auth.provider === "firebase" ? "Continue with GitHub" : "Sign in with Atlas"}
        </Button>
        {auth.error && <p className="error-text">{auth.error}</p>}
        {auth.emulator && <EmulatorSignIn />}
      </div>
    </div>
  );
}

/** Local development against the Auth emulator only. */
function EmulatorSignIn() {
  const auth = useAuth();
  const [email, setEmail] = useState("dev@repu.test");
  return (
    <form
      className="form emulator-signin"
      onSubmit={(e) => {
        e.preventDefault();
        void auth.signInTestAccount(email, email.split("@")[0] ?? email);
      }}
    >
      <p className="muted small">Auth emulator: sign in with a local test account.</p>
      <div className="inline-form">
        <input aria-label="Test account email" value={email} onChange={(e) => { setEmail(e.target.value); }} type="email" required />
        <Button type="submit">Use test account</Button>
      </div>
    </form>
  );
}

/** Sends the user to their first organisation, or offers to create one. */
export function Home() {
  const me = useApi<Me>("/v1/me");
  if (me.loading) return <Loading />;
  const first = me.data?.organisations[0];
  if (first) return <Navigate to={`/o/${first.slug}`} replace />;
  return <CreateOrganisation />;
}

function CreateOrganisation() {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const create = useAction(async () => {
    const org = await fetcher<Organisation>("/v1/orgs", { method: "POST", body: { slug, name } });
    void navigate(`/o/${org.slug}`);
  });
  return (
    <div className="centered">
      <form
        className="panel form"
        onSubmit={(e) => {
          e.preventDefault();
          void create.run();
        }}
      >
        <h1>Create an organisation</h1>
        <p className="muted">Usually named after your GitHub organisation. You become its owner.</p>
        <label>
          Name
          <input value={name} onChange={(e) => { setName(e.target.value); if (!slug) setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")); }} required placeholder="REPU-ME" />
        </label>
        <label>
          Slug
          <input value={slug} onChange={(e) => { setSlug(e.target.value); }} required pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?" placeholder="repu-me" />
        </label>
        <ErrorBox error={create.error} />
        <Button variant="primary" type="submit" busy={create.busy}>
          Create organisation
        </Button>
      </form>
    </div>
  );
}
